import { useEffect, useState } from 'react';
import { Box } from '@material-ui/core';
import { t, Trans } from '@lingui/macro';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { SORT_ASC } from '../../../utilities/FormUtilities';
import { useArrayLoader } from '../../data-table/Loaders';
import DataTable from '../../data-table/DataTable';
import FormSection from '../../form/FormSection';
import {
    COLLECTORS_FUNCTION_NAME,
    VCENTERS_ADMIN_VALUE_DESCRIPTION_FIELD,
    VCENTERS_ADMIN_VALUE_IPADDRESS_FIELD,
    VCENTERS_ADMIN_VALUE_NAME_FIELD,
    VCENTERS_ADMIN_VALUE_VIRTUALIZATION_ENABLEDENTITYCOUNT_FIELD,
} from './CollectorsAdminConstants';
import { buildPageTarget } from '../AdminLinkUtils';
import { useDataService } from '../../../services/ApiDataService';
import useAlerts from '../../../stores/AlertsStore';
import { VIRTUAL_INFRASTRUCTURE_FUNCTION_NAME } from '../infrastructure/InfrastructureConstants';

const vCenterUrl = '/ipm/v1/admin/virtualInfrastructureConfig/findVCenterByCollector';
const emptyvCentersGridText = t`This collector does not contain any vCenters to monitor`;

function getvCenterColumns() {
    return [
        {
            type: COLUMN_TYPES.LINK,
            mapping: VCENTERS_ADMIN_VALUE_NAME_FIELD,
            title: t`Name`,
            tooltip: t`The name of this vCenter`,
            width: 225,
            sortable: false,
            link: {
                sysAdminOnly: true,
                target: buildPageTarget(VIRTUAL_INFRASTRUCTURE_FUNCTION_NAME),
            },
        },
        {
            type: COLUMN_TYPES.LINK,
            mapping: VCENTERS_ADMIN_VALUE_IPADDRESS_FIELD,
            title: t`IP address`,
            tooltip: t`The IP of this vCenter`,
            width: 225,
            sortable: false,
            link: {
                sysAdminOnly: true,
                target: buildPageTarget(VIRTUAL_INFRASTRUCTURE_FUNCTION_NAME),
            },
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            mapping: VCENTERS_ADMIN_VALUE_VIRTUALIZATION_ENABLEDENTITYCOUNT_FIELD,
            title: t`Monitored Elements`,
            tooltip: t`The number of monitored elements in this vCenter`,
            sortable: false,
            width: 175,
            formatter: (value, row) => processMonitoredElements(value, row),
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: VCENTERS_ADMIN_VALUE_DESCRIPTION_FIELD,
            title: t`Description`,
            tooltip: t`The description of this vCenter`,
            sortable: false,
            flex: 1,
        },
    ];
}

const initialSort = [{ property: 'name', direction: SORT_ASC }];

function processMonitoredElements(value, row) {
    // eslint-disable-next-line no-bitwise
    let enabledEntityCount = value | 0;
    if (enabledEntityCount !== 0) {
        // eslint-disable-next-line no-plusplus
        enabledEntityCount++;
    } else if (enabledEntityCount === 0 && row.state === 'active' && !row.disabled && !row.deleted) {
        enabledEntityCount = 1;
    }

    return enabledEntityCount;
}

export function CollectorDetailvCenters({ collectorId }) {
    const [vCenterList, setvCenterList] = useState([]);
    const vCenterLoader = useArrayLoader(vCenterList, (data) => data);
    const dataService = useDataService();
    const [, alertsActions] = useAlerts();

    useEffect(() => {
        dataService
            .get(vCenterUrl, {
                query: JSON.stringify({
                    collectorUuid: collectorId,
                    $and: [{ deleted: false }],
                }),
                requestParams: JSON.stringify({
                    rowStart: 0,
                    rowLimit: 100,
                }),
            })
            .then((response) => setvCenterList(response.data))
            .catch((e) => {
                if (e?.responseBody?.error) {
                    alertsActions.createErrorAlert(e.responseBody.error);
                }
            });
    }, [collectorId, dataService, alertsActions]);

    return (
        <FormSection title={<Trans>vCenters</Trans>}>
            <Box mt={4}>
                <DataTable
                    tableId={`${COLLECTORS_FUNCTION_NAME}-vCenters`}
                    loader={vCenterLoader}
                    columns={getvCenterColumns()}
                    visibleRows={8}
                    showToolbar={false}
                    showSearch={false}
                    sorters={initialSort}
                    emptyText={emptyvCentersGridText}
                />
            </Box>
        </FormSection>
    );
}
